<template>
  <v-card
    outlined
    class="kanban-content-card px-4 py-3 d-flex flex-column justify-space-between kanban-drag-control mb-2 w-100"
    active-class="white"
    style="width: 100%"
    :data-status="source.status"
    :id="source.id"
    :ripple="false"
    @click.stop="handleClick"
  >
    <div :data-status="source.status">
      <div
        class="d-flex align-top align-self-top justify-space-between"
        :data-status="source.status"
      >
        <div
          class="d-flex align-top align-self-top"
          :data-status="source.status"
        >
          <div
            class="align-top align-self-top"
            style="line-height: 16px"
            :data-status="source.status"
          >
            <v-icon
              size="14"
              :color="isCompleted ? 'green' : 'blue-grey darken-1'"
              :data-status="source.status"
              @click.stop="handleTaskStatusClick"
            >
              {{ statusTaskIcon }}
            </v-icon>
          </div>
          <div
            class="ms-2 text-left me-3"
            :class="{ 'task-completed': isCompleted }"
            :data-status="source.status"
          >
            <p
              v-snip="2"
              :data-status="source.status"
              class="pa-0 ma-0 blue-grey--text text--darken-3 font-size-12 font-weight-500 entity-name"
            >
              {{ name }}
            </p>
          </div>
        </div>
        <div class="mt-n1" :data-status="source.status">
          <Menu :options="menuOptions">
            <template v-slot:activator="{ on }">
              <v-icon
                v-on="on"
                :size="16"
                color="blue-grey darken-1"
                :data-status="source.status"
                >mdi-dots-vertical</v-icon
              >
            </template>
          </Menu>
        </div>
      </div>
      <div
        :data-status="source.status"
        v-if="source.linkedEntityId"
        class="mt-2 mb-1"
        :class="{ 'task-completed': isCompleted }"
      >
        <v-chip
          small
          color="blue-grey lighten-3"
          class="linked-entity px-2"
          :data-status="source.status"
          @click.stop="navigateTo(linkedEntityRoute)"
        >
          <EntityIcon
            size="10"
            class="me-1"
            :entityType="source.linkedEntityType"
            :data-status="source.status"
          />
          <p
            class="ma-0 pa-0 blue--text text-truncate linked-entity-name font-size-8"
            :data-status="source.status"
          >
            {{ linkedEntityText }}
          </p>
        </v-chip>
      </div>
    </div>
    <div
      class="d-flex align-center justify-space-between mt-1"
      style="height: 26px"
      :data-status="source.status"
      :class="{ 'task-completed': isCompleted }"
    >
      <div class="d-flex" :data-status="source.status">
        <div
          class="flex-grow-0 d-flex me-3"
          v-if="dueDate && !isCompleted"
          :data-status="source.status"
        >
          <v-tooltip top>
            <template v-slot:activator="{ on }">
              <v-icon
                v-on="on"
                size="12"
                class="me-1"
                :data-status="source.status"
                :color="isOverdue ? 'red' : 'blue-grey darken-1'"
              >
                mdi-calendar
              </v-icon>
              <p
                v-on="on"
                class="ma-0 pa-0 font-size-10 d-flex flex-grow-1 align-center due-date-container"
                :data-status="source.status"
                :class="
                  isOverdue ? 'red--text' : 'blue-grey--text text--darken-1'
                "
              >
                {{ dueDateText }}
              </p>
            </template>
            {{ $t('common.due_date') }}
          </v-tooltip>
          <v-tooltip top>
            <template v-slot:activator="{ on }">
              <v-icon
                v-on="on"
                v-if="isOverdue"
                class="ms-1"
                size="12"
                color="red"
                :data-status="source.status"
                >mdi-alert-circle-outline</v-icon
              >
            </template>
            {{ $t('common.overdue') }}
          </v-tooltip>
        </div>
        <div class="ma-0 pa-0 d-flex" :data-status="source.status">
          <v-icon color="blue-grey" size="12" :data-status="source.status">
            {{ source.isPrivate ? 'mdi-lock' : 'mdi-earth' }}
          </v-icon>
          <p
            class="ma-0 pa-0 ms-1 font-size-10 blue-grey--text text--darken-1 font-weight-400"
            :data-status="source.status"
          >
            {{ source.isPrivate ? $t('tasks.private') : $t('tasks.public') }}
          </p>
        </div>
      </div>
      <div
        class="d-flex align-center justify-end"
        style="height: 100%"
        :data-status="source.status"
      >
        <PriorityIcon
          v-if="priority"
          :size="16"
          :priority="priority"
          :data-status="source.status"
        />
        <v-tooltip top>
          <template v-slot:activator="{ on }">
            <UserAvatar
              v-on="on"
              v-if="source.assignees[0]"
              size="24px"
              font-size="10px"
              class="ms-3"
              :data-status="source.status"
              :account="source.assignees[0]"
            />
          </template>
          {{
            `${$t('common.assignee_one')}: ${getFullName(source.assignees[0])}`
          }}
        </v-tooltip>
      </div>
    </div>
  </v-card>
</template>

<script>
import moment from 'moment'
import _ from 'lodash'
import { mapGetters } from 'vuex'
import { utils } from 'frontend-js-shared'

import trackerMixin from '@/mixins/tracking/trackerMixin.vue'
import revMixin from '@/mixins/revMixin'
import mixin from '@/mixins/mixin'
import userMixin from '@/mixins/userMixin.vue'

import api from '@/api'
import getEntityId from '@/lib/getEntityId'

import UserAvatar from '@/components/UserAvatar.vue'
import PriorityIcon from '@/components/PriorityIcon.vue'
import EntityIcon from '@/components/EntityIcon.vue'
import Menu from '@/components/UI/Menu.vue'

import eventHub, { HUB_EVENTS } from '@/lib/event-hub'

export default {
  name: 'KanbanCard',

  mixins: [trackerMixin, revMixin, mixin, userMixin],

  components: {
    UserAvatar,
    PriorityIcon,
    EntityIcon,
    Menu,
  },

  props: {
    source: Object,
  },

  data() {
    return {
      showContentDrawer: false,
    }
  },

  computed: {
    ...mapGetters(['organization', 'organizationId', 'accountId']),

    menuOptions() {
      return _.compact([
        this.iAmCreatorOrAssignee &&
          this.source?.status !== 'open' && {
            id: 'open',
            title: this.$t('tasks.kanban.reopen_task'),
            icon: 'mdi-arrow-left',
            action: () => {
              this.trackTasks('tasks.kanbanCardMenuClick', {
                option: 'move to open',
              })
              this.updateStatus('open')
            },
          },
        this.iAmCreatorOrAssignee &&
          this.source?.status === 'open' && {
            id: 'inProgress',
            title: this.$t('tasks.kanban.move_to_in_progress'),
            icon: 'mdi-arrow-right',
            action: () => {
              this.trackTasks('tasks.kanbanCardMenuClick', {
                option: 'move to in progress',
              })
              this.updateStatus('inProgress')
            },
          },
        this.iAmCreatorOrAssignee &&
          !this.isCompleted && {
            id: 'complete',
            title: this.$t('tasks.kanban.mark_as_completed'),
            icon: 'mdi-check',
            action: () => {
              this.trackTasks('tasks.kanbanCardMenuClick', {
                option: 'move to complete',
              })
              this.updateStatus('completed')
            },
          },
        {
          id: 'duplicate',
          title: this.$t('tasks.kanban.duplicate'),
          icon: 'mdi-content-copy',
          action: () => {
            this.trackTasks('tasks.kanbanCardMenuClick', {
              option: 'duplicate task',
            })
            this.duplicateTask()
          },
        },
        this.iAmCreatorOrAssignee && {
          id: 'delete',
          title: this.$t('tasks.kanban.delete'),
          icon: 'mdi-trash-can-outline',
          action: () => {
            this.trackTasks('tasks.kanbanCardMenuClick', {
              option: 'delete task',
            })
            this.deleteTask()
          },
        },
      ])
    },

    isCompleted() {
      return this.source?.status === this.$constants.tasksStatus.completed
    },

    statusTaskIcon() {
      return this.isCompleted ? 'mdi-check-circle' : 'mdi-radiobox-blank'
    },

    linkedEntityText() {
      const customName =
        this.organization?.customNamings?.[this.source.linkedEntityType]
          ?.singular ?? null

      const entityName = this.$t(
        `common.${_.snakeCase(this.source.linkedEntityType)}_one`
      )

      return this.$t('tasks.link_to_entity', {
        entity: customName || entityName,
      })
    },

    linkedEntityRoute() {
      return this.generateRevRoute(
        this.organizationId,
        this.source.linkedEntityType,
        this.source.linkedEntityId
      )
    },

    createdBy() {
      return this.source.createdBy
    },

    createdAt() {
      return this.source.createdAt
    },

    priority() {
      return this.source.priority
    },

    dueDate() {
      return this.source.dueDate
    },

    name() {
      return this.source.name
    },

    dueDateText() {
      return utils.dates.humanDueDate(this.dueDate) ?? ''
    },

    entityType() {
      return this.source.entityType
    },

    isOverdue() {
      const today = moment()
        .add(0, 'days')
        .format('YYYY-MM-DD')
      const dueDate = moment(this.dueDate).format('YYYY-MM-DD')

      return dueDate < today
    },

    iAmCreatorOrAssignee() {
      const iAmCreator = this.accountId === this.source.createdBy?.id
      const iAmAssignee = Boolean(
        this.source.assignees?.find(assignee => assignee.id === this.accountId)
      )
      return iAmCreator || iAmAssignee
    },
  },

  methods: {
    async updateStatus(newStatus) {
      eventHub.$emit(HUB_EVENTS.TASK_KANBAN.UPDATED, {
        id: this.source.id,
        oldStatus: this.source.status,
        newStatus,
      })
    },

    async duplicateTask() {
      try {
        const response = await api.tasks.create(this.organizationId, {
          ...this.source,
          status: 'open',
          name: `${this.source.name} ${this.$t('tasks.copy')}`,
          site: getEntityId(this.source),
          assignees: this.source.assignees.map(assignee =>
            getEntityId(assignee)
          ),
        })

        eventHub.$emit(HUB_EVENTS.TASK_EDITION_DRAWER.UPDATE, response.data.id)
        eventHub.$emit(HUB_EVENTS.TASK_KANBAN.DUPLICATED, response.data)
      } catch (err) {
        this.handleRequestError(err)
      }
    },

    async deleteTask() {
      try {
        await api.tasks.delete(this.organizationId, this.source.id)

        eventHub.$emit(HUB_EVENTS.TASK_KANBAN.DELETED, this.source.status)
      } catch (err) {
        this.handleRequestError(err)
      }
    },

    async handleTaskStatusClick() {
      const newStatus =
        this.source?.status !== this.$constants.tasksStatus.completed
          ? this.$constants.tasksStatus.completed
          : this.$constants.tasksStatus.open

      this.trackTasks('tasks.kanbanCardStatusClick', {
        newStatus,
      })
      this.updateStatus(newStatus)
    },

    handleClick() {
      const taskId = this.source.id
      this.trackTasks('tasks.kanbanCardClick')
      this.replaceRoute({
        path: this.$route.fullPath,
        query: {
          ...this.$route.query,
          task: taskId,
        },
      })
      eventHub.$emit(HUB_EVENTS.TASK_EDITION_DRAWER.UPDATE, taskId)
    },
  },
}
</script>

<style scoped lang="scss">
.task-completed {
  opacity: 0.5;
}

.entity-name {
  line-height: 16px;
}

.kanban-content-card {
  .due-date-container {
    line-height: 8px;
  }
}
</style>
<style lang="scss">
.linked-entity-name:hover {
  text-decoration: underline $color-blue;
}
.card-hover:hover {
  .linked-entity {
    background-color: $color-blue-grey-lighten2 !important;
  }
}
</style>
